import { useInvalidateControlsCache } from 'src/components/async/model/control'
import { ControlServicePromiseClient } from '@trustero/trustero-api-web/lib/model/control_grpc_web_pb'
import { ToggleUsesNewAuditScanRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import log from 'loglevel'
import { useCallback } from 'react'
import { useConfirmationModal } from 'src/components/ModalForms'
import {
  useCreateControlTestProcedure,
  useDeleteControlTestProcedure,
  useUpdateControlSuggestedEvidence,
  useUpdateControlSuggestedTests,
} from 'src/components/async/model/control/useControlMutations'
import { useAuthorizedGrpcClient } from '../../../adapter'

export const useToggleUsesNewAuditScan = (): ((
  controlId: string,
  usesNewAuditScan: boolean,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ControlServicePromiseClient)
  const mutate = useInvalidateControlsCache()

  return useCallback(
    async (controlId: string, usesNewAuditScan: boolean) => {
      try {
        const request = new ToggleUsesNewAuditScanRequest()
          .setControlId(controlId)
          .setUsesNewAuditScan(usesNewAuditScan)
        await client.toggleUsesNewAuditScan(request)
      } catch (error) {
        log.error('Error toggling uses new audit scan', error)
      } finally {
        await mutate()
      }
    },
    [client, mutate],
  )
}

export const useToggleUsesNewAuditScanModal = (
  controlId: string,
  usesNewAuditScan: boolean,
): (() => Promise<void>) => {
  const toggleUsesNewAuditScan = useToggleUsesNewAuditScan()

  // use confirmation modal to toggle uses new audit scan
  const openConfirmationModal = useConfirmationModal({
    title: usesNewAuditScan
      ? 'Disable advanced examination and test?'
      : 'Use advanced examination and test?',
    body: usesNewAuditScan
      ? 'Are you sure you want to disable advanced examination and test for this control?'
      : 'Are you sure you want to use advanced examination and test for this control?',
    confirmText: usesNewAuditScan
      ? 'Disable advanced examination and test'
      : 'Use advanced examination and test',
    onConfirmCB: async () => {
      await toggleUsesNewAuditScan(controlId, !usesNewAuditScan)
    },
  })

  return useCallback(async () => {
    openConfirmationModal()
  }, [openConfirmationModal])
}

export type ImportedTestProcedureCopyPayload = {
  id: string
  title: string
  body: string
  position: number
}

export const useNotifyImportedTestProcedureCopy = (): ((
  controlId: string,
  testProcedures: ImportedTestProcedureCopyPayload[],
) => Promise<void>) => {
  return useCallback(
    async (
      _controlId: string,
      _testProcedures: ImportedTestProcedureCopyPayload[],
    ) => {
      // TODO: call backend endpoint to notify copied imported test procedures.
      return
    },
    [],
  )
}

// Re-export with the same names for backward compatibility with ControlsShowPageCards imports
export const useUpdateSuggestedTests = useUpdateControlSuggestedTests
export const useUpdateSuggestedEvidence = useUpdateControlSuggestedEvidence
export { useCreateControlTestProcedure, useDeleteControlTestProcedure }
